import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import useTranslation from '../../../hooks/useTranslation';
import { notesServiceFor, NOTE_MAX_LENGTH } from '../../../services/notesService';

/**
 * Notes internes, generiques : extras / credits (extra_notes) et directives
 * (directive_notes), selon `entityType`. Un seul composant pour les deux
 * tableaux : meme icone, meme infobulle, meme popover.
 *
 * Icone carnet bleue ; pastille compteur s'il y a des notes. Survol : la
 * derniere note. Clic : fil complet (plus recent en haut), ajout en bas,
 * modifier / supprimer seulement ses propres notes. Jamais dans le PDF.
 *
 * `summary` = { count, last } vient d'une seule requete pour tout le tableau ;
 * le fil se charge a l'ouverture.
 *
 * Le popover a son propre portail (maquette de Mazine : carte arrondie, titre
 * et notes en bleu) : le cadre de PortalDropdown, partage, ne s'y prete pas.
 */

const POPOVER_WIDTH = 400;
const MARGIN = 8;

const fmtDateTime = (value, language) => {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat(language === 'en' ? 'en-CA' : 'fr-CA', { dateStyle: 'medium', timeStyle: 'short' })
      ?.format(new Date(value));
  } catch {
    return '';
  }
};

const NotesButton = ({ entityType = 'extra', entityId, projectId, subtitle, summary, onChanged }) => {
  const service = notesServiceFor(entityType);
  const { t, language } = useTranslation();
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const tooltipTimer = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [userId, setUserId] = useState(null);

  const count = summary?.count || 0;
  const last = summary?.last || null;
  // Fermer remet aussi l'infobulle a zero : sinon elle restait affichee apres
  // la fermeture (le survol du popover, rendu dans un portail, la declenchait).
  const close = useCallback(() => {
    clearTimeout(tooltipTimer?.current);
    setShowTooltip(false);
    setOpen(false);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rows, uid] = await Promise.all([service.list(entityId), service.getCurrentUserId()]);
      setNotes(rows || []);
      setUserId(uid);
    } catch (err) {
      console.error(`[notes:${entityType}] lecture impossible:`, err);
      setError(t('extras.notes.loadError'));
    } finally {
      setLoading(false);
    }
  }, [entityId, service, t]);

  useEffect(() => {
    if (open) load();
    else { setEditingId(null); setConfirmDeleteId(null); setError(null); }
  }, [open, load]);

  useEffect(() => () => clearTimeout(tooltipTimer?.current), []);

  // Position : sous l'icone, aligne a droite ; au-dessus si la place manque.
  useLayoutEffect(() => {
    if (!open || !btnRef?.current) return;
    const rect = btnRef?.current?.getBoundingClientRect();
    const height = popRef?.current?.offsetHeight || 0;
    const left = Math.max(MARGIN, Math.min(rect?.right - POPOVER_WIDTH, window.innerWidth - POPOVER_WIDTH - MARGIN));
    let top = rect?.bottom + 6;
    if (height && top + height > window.innerHeight - MARGIN) {
      top = rect?.top - 6 - height >= MARGIN ? rect?.top - 6 - height : Math.max(MARGIN, window.innerHeight - height - MARGIN);
    }
    setPos((prev) => (prev?.top === top && prev?.left === left ? prev : { top, left }));
  }, [open, notes, loading, editingId, confirmDeleteId, error]);

  // Fermeture : clic exterieur, Echap, defilement de la page (pas du popover).
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!btnRef?.current?.contains(e?.target) && !popRef?.current?.contains(e?.target)) close();
    };
    const onKey = (e) => { if (e?.key === 'Escape') close(); };
    const onScroll = (e) => { if (!popRef?.current?.contains(e?.target)) close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open, close]);

  const errorText = (err) => (
    err?.message === 'NOTE_TOO_LONG' ? t('extras.notes.tooLong', { max: NOTE_MAX_LENGTH })
      : err?.message === 'NOTE_NOT_OWN' ? t('extras.notes.notOwn')
      : t('extras.notes.saveError')
  );

  const handleAdd = async () => {
    if (!draft?.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      await service.create({ entityId, projectId, body: draft });
      setDraft('');
      await load();
      onChanged?.();
    } catch (err) {
      console.error(`[notes:${entityType}] ajout impossible:`, err);
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (noteId) => {
    if (!editText?.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      await service.update(noteId, editText);
      setEditingId(null);
      await load();
      onChanged?.();
    } catch (err) {
      console.error(`[notes:${entityType}] modification impossible:`, err);
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    setSaving(true);
    setError(null);
    try {
      await service.remove(noteId);
      setConfirmDeleteId(null);
      await load();
      onChanged?.();
    } catch (err) {
      console.error(`[notes:${entityType}] suppression impossible:`, err);
      setError(errorText(err));
    } finally {
      setSaving(false);
    }
  };

  // Ctrl+Entree (ou Cmd+Entree) enregistre.
  const submitOnCtrlEnter = (action) => (e) => {
    if (e?.key === 'Enter' && (e?.ctrlKey || e?.metaKey)) {
      e?.preventDefault();
      action();
    }
  };

  const hasNotes = count > 0;
  const textareaClass =
    'w-full px-3 py-2.5 text-sm leading-relaxed bg-white border border-gray-200 rounded-xl outline-none resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-gray-400';

  const popover = open && createPortal(
    <div
      ref={popRef}
      role="dialog"
      aria-label={t('extras.notes.title')}
      style={{ position: 'fixed', top: pos?.top, left: pos?.left, width: POPOVER_WIDTH, zIndex: 9999 }}
      className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden whitespace-normal text-left"
      // Les clics restent dans le popover : ils n'ouvrent pas la fiche de la ligne.
      onClick={(e) => e?.stopPropagation()}
      onMouseDown={(e) => e?.stopPropagation()}
    >
      {/* En-tete */}
      <div className="flex items-start justify-between gap-3 px-5 pt-4 pb-3 border-b border-gray-100">
        <div className="min-w-0">
          <p className="text-[15px] font-semibold text-blue-600 leading-tight">{t('extras.notes.title')}</p>
          <p className="mt-0.5 text-xs text-gray-500 truncate">{subtitle || '—'}</p>
        </div>
        <button
          type="button"
          onClick={close}
          aria-label={t('common.close')}
          className="p-1 -mr-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors flex-shrink-0"
        >
          <Icon name="X" size={16} />
        </button>
      </div>

      {/* Fil : plus recent en haut */}
      <div className="max-h-[320px] overflow-y-auto divide-y divide-gray-100">
        {loading ? (
          <div className="flex justify-center py-6">
            <Icon name="Loader2" size={16} className="animate-spin text-primary" />
          </div>
        ) : notes?.length === 0 ? (
          <p className="text-xs text-gray-400 text-center py-6">{t('extras.notes.empty')}</p>
        ) : (
          notes?.map((note) => {
            const own = userId && note?.created_by === userId;
            const edited = note?.updated_at && note?.created_at && new Date(note?.updated_at) - new Date(note?.created_at) > 1000;
            return (
              <div key={note?.id} className="px-5 py-3">
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <span className="font-semibold text-gray-500">{note?.authorName || '—'}</span>
                  <span>·</span>
                  <span>{fmtDateTime(note?.created_at, language)}</span>
                  {edited && <span className="italic text-gray-400">· {t('extras.notes.edited')}</span>}
                  {own && editingId !== note?.id && confirmDeleteId !== note?.id && (
                    <span className="ml-auto flex items-center gap-1 text-gray-300">
                      <button
                        type="button"
                        onClick={() => { setEditingId(note?.id); setEditText(note?.body || ''); setConfirmDeleteId(null); }}
                        title={t('extras.notes.edit')}
                        aria-label={t('extras.notes.edit')}
                        className="p-0.5 rounded text-gray-400 hover:text-gray-700"
                      >
                        <Icon name="Pencil" size={12} />
                      </button>
                      <span>·</span>
                      <button
                        type="button"
                        onClick={() => { setConfirmDeleteId(note?.id); setEditingId(null); }}
                        title={t('extras.notes.delete')}
                        aria-label={t('extras.notes.delete')}
                        className="p-0.5 rounded text-gray-400 hover:text-red-600"
                      >
                        <Icon name="Trash2" size={12} />
                      </button>
                    </span>
                  )}
                </div>

                {editingId === note?.id ? (
                  <div className="mt-1.5 space-y-1.5">
                    <textarea
                      rows={3}
                      autoFocus
                      value={editText}
                      maxLength={NOTE_MAX_LENGTH}
                      onChange={(e) => setEditText(e?.target?.value)}
                      onKeyDown={submitOnCtrlEnter(() => handleUpdate(note?.id))}
                      className={textareaClass}
                    />
                    <div className="flex justify-end gap-1.5">
                      <button
                        type="button"
                        onClick={() => setEditingId(null)}
                        className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg"
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        type="button"
                        disabled={saving || !editText?.trim()}
                        onClick={() => handleUpdate(note?.id)}
                        className="px-3 py-1.5 text-xs font-semibold text-white bg-primary hover:bg-primary-dark rounded-lg disabled:opacity-50"
                      >
                        {t('extras.notes.save')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="mt-1 text-sm text-blue-600 leading-relaxed whitespace-pre-wrap break-words">{note?.body}</p>
                )}

                {confirmDeleteId === note?.id && (
                  <div className="mt-2 flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-red-50 border border-red-100">
                    <span className="text-xs text-red-700">{t('extras.notes.deleteConfirm')}</span>
                    <span className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(null)}
                        className="px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-white rounded-md"
                      >
                        {t('common.cancel')}
                      </button>
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => handleDelete(note?.id)}
                        className="px-2.5 py-1 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                      >
                        {t('extras.notes.delete')}
                      </button>
                    </span>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Ajout */}
      <div className="px-5 pt-4 pb-4 bg-gray-50 border-t border-gray-100 space-y-2.5">
        {error && <p className="text-xs text-red-600">{error}</p>}
        <textarea
          rows={3}
          value={draft}
          maxLength={NOTE_MAX_LENGTH}
          onChange={(e) => setDraft(e?.target?.value)}
          onKeyDown={submitOnCtrlEnter(handleAdd)}
          placeholder={t('extras.notes.placeholder')}
          className={textareaClass}
        />
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-gray-500 leading-snug">{t('extras.notes.visibility')}</span>
          <button
            type="button"
            disabled={saving || !draft?.trim()}
            onClick={handleAdd}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors disabled:opacity-50 flex-shrink-0"
          >
            {saving && <Icon name="Loader2" size={13} className="animate-spin" />}
            {t('extras.notes.add')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        // Survol de l'icone seulement (pas du popover) pour l'infobulle.
        onMouseEnter={() => { if (!open) tooltipTimer.current = setTimeout(() => setShowTooltip(true), 200); }}
        onMouseLeave={() => { clearTimeout(tooltipTimer?.current); setShowTooltip(false); }}
        onClick={() => { clearTimeout(tooltipTimer?.current); setShowTooltip(false); setOpen((v) => !v); }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t('extras.notes.title')}
        className={`relative p-1 rounded-md transition-colors group ${open ? 'bg-gray-100 ring-1 ring-gray-200' : ''}`}
      >
        <Icon
          name="NotebookPen"
          size={13}
          className="transition-colors text-blue-500 group-hover:text-blue-700"
        />
        {hasNotes && (
          <span
            className="absolute -top-1 -right-1 text-[10px] rounded-full px-1 leading-tight font-medium bg-blue-100 text-blue-600"
            style={{ minWidth: '14px', textAlign: 'center' }}
          >
            {count}
          </span>
        )}
      </button>

      {/* Survol : la derniere note */}
      {showTooltip && !open && (
        <div className="absolute right-0 top-full mt-1.5 z-50 bg-white shadow-md rounded-md px-2.5 py-2 w-[240px] pointer-events-none whitespace-normal">
          {last ? (
            <>
              <p className="text-[11px] text-blue-600 leading-snug line-clamp-2">{last?.body}</p>
              <p className="mt-1 text-[10px] text-gray-400">
                {last?.authorName || '—'} · {fmtDateTime(last?.created_at, language)}
              </p>
            </>
          ) : (
            <p className="text-[10px] text-gray-500">{t('extras.notes.none')}</p>
          )}
        </div>
      )}

      {popover}
    </div>
  );
};

export default NotesButton;
